/*
 * The canonical Delivery chart model — the ONE shape DeliveryChart draws.
 *
 * Two governed states, one visualization:
 *
 *   buildTimeSeriesDeliveryChartModel  — a certified series (Quantum's daily /
 *       weekly / cumulative buckets): stacked platform segments per time
 *       bucket, the stepped plan, the modeled projection, the GA4 site-visit
 *       overlay, the efficiency panel and the indexed comparison.
 *
 *   buildWindowTotalsDeliveryChartModel — window totals only (authenticated
 *       Cortex Platform Analytics and the public report today): one certified
 *       value per platform for the selected window, with the platform SET on
 *       the x-axis. Never time — a window total drawn on a time axis would read
 *       as a daily series that was never reported.
 *
 *   buildPlatformDeliveryChartModel — ONE platform's own Analytics page
 *       (/youtube-analytics, /ctv-analytics, …). Same two states as above,
 *       decided by the page's governed view model: dated columns when that
 *       platform's source reports by day, a single window-total column when it
 *       does not. One segment per column, because the page reports on one
 *       platform — that is the only thing that distinguishes it.
 *
 * Both return the same shape, so the window-total state is the canonical chart
 * with fewer series rather than a second chart. Neither builder invents a
 * value: what the caller's canonical payload does not carry simply is not on
 * the model, and the surface declares its absence in words.
 *
 * Resolvers and formatters are injected because the two portals resolve
 * platform identity and format metrics through different governed modules
 * (Quantum's cross-platform contract; the shared platform-analytics report
 * model). Injecting them is what lets this file — and the renderer — be one
 * implementation shared by both repositories.
 */

import {
  resolveClientSpendBasisLabel,
  toClientSafeBasisText,
} from "./spendEfficiencyContract";

export const DELIVERY_CHART_AXES = Object.freeze({
  TIME: "time",
  PLATFORM: "platform",
});

const FALLBACK_COLOR = "var(--cphere-cyan, #7ee7ff)";

/**
 * Quantum's governed cross-platform view model → the canonical chart model.
 *
 * `viewModel` is the output of buildCrossPlatformViewModel; the three resolver
 * functions come from the cross-platform contract.
 */
export function buildTimeSeriesDeliveryChartModel({
  viewModel,
  resolvePlatform,
  resolveMetric,
  formatMetricValue,
}) {
  if (!viewModel) return null;
  /*
   * Client vocabulary for the plan legend. Never `viewModel.spendBasisLabel`,
   * which is the contract's internal label; `toClientSafeBasisText` also
   * rewrites a legacy cached label so a hydrated chart cannot redisplay the
   * old wording.
   */
  const planBasisLabel =
    resolveClientSpendBasisLabel(viewModel) || toClientSafeBasisText(viewModel.spendBasisLabel);
  const volumeMetric = resolveMetric(viewModel.volumeMetricKey);
  const efficiencyMetric = resolveMetric(viewModel.efficiencyMetricKey);
  const volumeUnit = volumeMetric?.unit || "count";
  const platformKeys = viewModel.platformKeys || [];
  const buckets = viewModel.buckets || [];

  return {
    columnAxis: DELIVERY_CHART_AXES.TIME,
    volume: { key: viewModel.volumeMetricKey, label: volumeMetric?.label || "Metric" },
    formatVolume: (value, options) => formatMetricValue(volumeUnit, value, options),
    formatPlan: (value, options) => formatMetricValue("currency", value, options),
    formatSiteVisits: (value, options) => formatMetricValue("count", value, options),
    showTotals: true,
    columns: buckets.map((bucket) => ({
      key: bucket.key,
      label: bucket.label,
      // Time buckets are not a platform, so they carry no focus identity.
      platformKey: null,
      valueLabel: null,
      segments: platformKeys
        .map((platformKey) => {
          const entry = bucket.byPlatform?.[platformKey];
          const value = entry?.[viewModel.volumeMetricKey];
          if (value == null) return null;
          const platform = resolvePlatform(platformKey);
          return {
            key: platformKey,
            label: platform?.label || platformKey,
            color: platform?.color || FALLBACK_COLOR,
            value,
          };
        })
        .filter(Boolean),
      planMedia: bucket.planMedia || 0,
      siteVisits: bucket.siteVisits ?? null,
      /*
       * A bucket may carry its own governed tooltip rows — the per-platform
       * efficiency reading for THAT bucket's counts, on surfaces whose
       * platforms are read on different metrics and therefore have no
       * chart-wide efficiency series to hover. Supplied by the caller,
       * already formatted; this builder never derives a rate of its own.
       */
      extraRows: Array.isArray(bucket.extraRows) ? bucket.extraRows : [],
    })),
    /*
     * The efficiency channel exists only when there is a comparable series to
     * put in it. An object carrying `comparable: false` and an empty series is
     * not a series — it is the ABSENCE of one wearing the shape of one, and a
     * reader of this model should be able to ask `if (model.efficiency)` and
     * get the truth. Surfaces that read efficiency per platform put it on the
     * legend entries instead, which is what keeps a rate from being stated in
     * two places at once.
     */
    efficiency: (() => {
      const series = viewModel.efficiencySeries || [];
      if (!viewModel.efficiencyComparable || !series.some((value) => value != null)) {
        return null;
      }
      return {
        label: efficiencyMetric?.label || "Efficiency",
        definition: efficiencyMetric?.definition || "",
        comparable: true,
        series,
        priorSeries: viewModel.priorEfficiencySeries || [],
        format: (value) => formatMetricValue(efficiencyMetric?.unit || "percent", value),
      };
    })(),
    indexed: (viewModel.indexedSeries || []).map((entry) => {
      const platform = resolvePlatform(entry.platformKey);
      return {
        key: entry.platformKey,
        label: platform?.label || entry.platformKey,
        color: platform?.color || FALLBACK_COLOR,
        points: entry.points || [],
      };
    }),
    projection: viewModel.projection || null,
    /*
     * The plan is available when a plan was actually supplied — derived, not
     * asserted. The renderer only draws the step where planMedia > 0, but the
     * LEGEND reads this flag, so asserting it unconditionally named a plan
     * series on surfaces that carry no plan at all.
     */
    plan: {
      available: buckets.some((bucket) => Number(bucket?.planMedia) > 0),
      /*
       * Client vocabulary only. This rendered "Plan (gross · client-billed)"
       * by lower-casing the contract's INTERNAL label — the one string on this
       * chart a client must never read. The client label is used when there is
       * one, and the legend falls back to a bare "Plan" rather than naming a
       * basis it cannot safely name.
       */
      label: planBasisLabel ? `Plan (${planBasisLabel.toLowerCase()})` : "Plan",
    },
    siteVisits: {
      available: Boolean(viewModel.siteVisits?.available),
      label: viewModel.siteVisits?.label || "Site Visits · GA4",
    },
    legend: platformKeys.map((platformKey) => {
      const platform = resolvePlatform(platformKey);
      return {
        key: platformKey,
        label: platform?.label || platformKey,
        color: platform?.color || FALLBACK_COLOR,
        /*
         * Optional: this platform's own governed efficiency reading for the
         * window, as `{ label, value }` with the value already formatted by
         * the surface. Supplied by surfaces that read efficiency per platform
         * rather than assuming one metric for all of them — a CTV entry says
         * VVR because CTV is not bought on clicks.
         *
         * Absent for callers that state efficiency as a chart-wide series
         * instead, which is how the two can never both render.
         */
        rate: viewModel.platformRates?.[platformKey] || null,
      };
    }),
    textSummary: viewModel.chartTextSummary || "",
  };
}

/**
 * Canonical window totals → the same chart model, platform axis.
 *
 * `platforms` are the report model's platform rows (already Meta-rolled-up,
 * measurement-only channels already excluded, zero rows already dropped).
 * `formatValue(value, kind)` and `resolveBranding(key)` are the caller's
 * governed formatting and platform identity.
 */
export function buildWindowTotalsDeliveryChartModel({
  platforms = [],
  metric,
  resolveBranding,
  formatValue,
  window: reportingWindow = null,
  // The governed rate pair. Both must be supplied together or the per-platform
  // rate is omitted: this file will not invent a second way to derive or print
  // a rate that a surface already states elsewhere.
  deriveRate = null,
  formatRate = null,
  /*
   * Optional: the surface's own per-platform efficiency reading, as
   * `(row) => ({ label, value }) | null` with `value` already formatted.
   *
   * Supplied by surfaces that read efficiency per platform rather than
   * assuming CTR everywhere — a CTV row is read on video view rate, and its
   * true, reported, meaningless 0.00% CTR is not a performance statement.
   * When supplied it REPLACES the deriveRate/formatRate pair, so a rate is
   * still emitted exactly once.
   */
  resolveRate = null,
}) {
  const metricKey = metric?.key || "spend";
  const metricKind = metric?.kind || "currency";
  const metricLabel = metric?.label || "Spend";
  /* Options pass through so an axis tick can ask for its own precision;
     the value label above each column is the certified figure, in full. */
  const format = (value, options) => formatValue(value, metricKind, options);
  const colorFor = (key) => resolveBranding(key)?.color || FALLBACK_COLOR;
  const ratesAvailable =
    typeof deriveRate === "function" && typeof formatRate === "function";

  const columns = platforms.map((row) => {
    const value = Math.max(0, row[metricKey] || 0);
    return {
      key: row.key,
      label: row.label,
      // Each column IS a platform here, which is what makes the column itself
      // the focus control.
      platformKey: row.key,
      valueLabel: format(value),
      segments: [
        { key: row.key, label: row.label, color: colorFor(row.key), value },
      ],
      planMedia: 0,
      siteVisits: null,
      // Efficiency has no trend to draw across an unordered platform set, but
      // the certified per-platform rate is still evidence, so it rides the
      // tooltip rather than being invented as a line. Derived and printed by
      // the caller's governed rate resolution — the same one the KPI strip
      // uses — so a platform's rate reads identically wherever it appears.
      //
      // ONE row, from ONE source. The rate used to be emitted here AND as an
      // efficiency series at the same time, which put two identically-labelled
      // CTR rows in the same tooltip.
      extraRows: (() => {
        if (typeof resolveRate === "function") {
          const resolved = resolveRate(row);
          return resolved ? [{ label: resolved.label, value: resolved.value }] : [];
        }
        if (!ratesAvailable) return [];
        const rate = deriveRate(row.clicks, row.impressions);
        return rate == null ? [] : [{ label: "CTR", value: formatRate(rate) }];
      })(),
    };
  });

  const windowPhrase = reportingWindow?.startDate
    ? `${reportingWindow.startDate} → ${reportingWindow.endDate}`
    : "the selected window";

  return {
    columnAxis: DELIVERY_CHART_AXES.PLATFORM,
    volume: { key: metricKey, label: metricLabel },
    formatVolume: format,
    formatPlan: format,
    formatSiteVisits: (value, options) => formatValue(value, "count", options),
    // One segment per column: a "Total" row would just repeat it.
    showTotals: false,
    columns,
    /*
     * No certified daily series means no efficiency TREND, no indexed
     * comparison, no plan step, no projection and no site-visit overlay. They
     * are absent from the model rather than drawn from something else.
     *
     * There is no chart-wide efficiency series here AT ALL, and that is the
     * point. The per-platform rate already rides each column's tooltip row; a
     * second copy of the same numbers as a plotted series is how the same
     * platform ended up stating its rate twice, under the same label, in one
     * tooltip. And across an unordered platform set — where one column may be
     * read on CTR and the next on video view rate — a single series would be a
     * blend of definitions that do not share a unit.
     */
    efficiency: null,
    indexed: [],
    projection: null,
    plan: { available: false, label: "" },
    siteVisits: { available: false },
    legend: columns.map((column) => ({
      key: column.key,
      label: column.label,
      color: column.segments[0].color,
    })),
    textSummary: columns.length
      ? `${metricLabel} reported for ${windowPhrase}, as window totals per platform: ${columns
          .map((column) => `${column.label} ${column.valueLabel}`)
          .join(", ")}. The x-axis is the platform set, not time — no daily ` +
        `series is certified for this scope, and nothing between platforms is interpolated.`
      : "",
  };
}

/**
 * ONE platform's governed page view model → the same canonical chart model.
 *
 * `chart` is `viewModel.chart` from platformAnalyticsViewModel: the buckets the
 * page decided to draw, already reconciled against the window KPI above them,
 * already carrying whatever this platform reported on each day and whatever GA4
 * observed on it. `platform` is `{ key, label, color }` — the page's own
 * identity and accent. `formatValue(unit, value, options)` is the governed
 * platform-metric formatter, injected for the same reason the other two
 * builders inject theirs.
 *
 * WHAT MAKES THIS ONE PLATFORM'S CHART, and nothing else:
 *
 *  - One segment per column. There is no stack to build, so there is no Total
 *    row either — it would only repeat the segment above it.
 *  - The columns are DELIVERY. A day GA4 observed but this platform did not
 *    deliver on is not given a column here: the page reports this platform, and
 *    a column with no media in it would be a delivery claim the source never
 *    made. Those observations are counted and declared in the spoken summary
 *    instead of being silently dropped.
 *  - Site visits ride the right-hand axis, and ONLY on a dated axis. A window
 *    total has no time for a series to be a series across.
 */
export function buildPlatformDeliveryChartModel({
  chart,
  platform,
  formatValue,
  siteVisitsAvailable = false,
}) {
  if (chart?.performance && !chart.buckets?.length) return { performance: chart.performance, textSummary: chart.textSummary };
  if (!chart || !Array.isArray(chart.buckets) || !chart.buckets.length) return null;

  const dated = chart.mode === "daily";
  const metricUnit = chart.metricUnit || "count";
  const metricLabel = chart.metricLabel || chart.metricKey || "Metric";
  const format = (value, options) => formatValue(metricUnit, value, options);
  const color = platform?.color || FALLBACK_COLOR;
  const label = platform?.label || platform?.key || "Platform";

  const efficiency = chart.efficiency || null;
  const efficiencySeries = efficiency?.points || [];

  const columns = chart.buckets.map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    /*
     * A dated column is a date, not a platform, so it carries no focus
     * identity. The single window-total column IS this platform, and naming it
     * is what lets the renderer print the certified value above it.
     */
    platformKey: dated ? null : platform?.key || null,
    valueLabel: dated ? null : format(bucket.value, { compact: true }),
    segments: [{ key: platform?.key || "platform", label, color, value: bucket.value }],
    planMedia: 0,
    /*
     * Attached by the view model from the observation's own date. Null here is
     * "GA4 reported nothing for this date", which the renderer draws as a break
     * in the line rather than a point at zero.
     */
    siteVisits: dated ? bucket.siteVisits ?? null : null,
    /*
     * Everything else this platform reported on this day, already resolved and
     * formatted by the page. Only metrics the day actually carries are present.
     */
    extraRows: (bucket.rows || []).map((row) => ({
      label: row.label,
      value: formatValue(row.unit, row.value, { compact: true }),
    })),
  }));

  return {
    performance: chart.performance || null,
    columnAxis: dated ? DELIVERY_CHART_AXES.TIME : DELIVERY_CHART_AXES.PLATFORM,
    volume: { key: chart.metricKey, label: metricLabel },
    formatVolume: format,
    formatPlan: format,
    /* Site visits are people, not this page's volume metric — counts, always. */
    formatSiteVisits: (value, options) => formatValue("count", value, options),
    // One segment per column: a "Total" row would just repeat it.
    showTotals: false,
    columns,
    efficiency:
      efficiency && efficiencySeries.some((value) => value != null)
        ? {
            label: efficiency.label,
            definition: efficiency.definition || "",
            comparable: true,
            series: efficiencySeries,
            priorSeries: [],
            format: (value) => formatValue(efficiency.unit || "percent", value),
          }
        : null,
    indexed: [],
    projection: null,
    plan: { available: false, label: "" },
    /*
     * Requested by the page (does this surface get the overlay at all) AND
     * observed in the data (did GA4 report anything on a date this chart
     * draws). Both, because either alone would either hide a real observation
     * or reserve an axis for a series that is not there.
     */
    siteVisits: {
      available:
        dated &&
        Boolean(siteVisitsAvailable) &&
        columns.some((column) => column.siteVisits != null),
      label: chart.siteVisits?.label || "Site Visits · GA4",
    },
    legend: [{ key: platform?.key || "platform", label, color }],
    textSummary: chart.textSummary || "",
  };
}
